import { TFunction } from "i18next"
import { cloneDeep } from "lodash-es"
import { action, computed, makeObservable, observable } from "mobx"
import { undefined } from "zod"

import { AppPricingInterval, CurrentDiscountCode } from "@/api/account/type.d"
import { APICode } from "@/api/APICode"
import appApi from "@/api/app"
import dashboard from "@/api/dashboard"
import { SetupGuideData } from "@/api/dashboard/type"
import settingsApi from "@/api/settings"
import i18n from "@/i18n/config"
import { getLocaleByShopifyCode } from "@/i18n/resource"
import { isSupportedLanguage, SupportLanguages } from "@/i18n/SupportLanguages"
import {
  BannerEnum,
  BetaFeature,
  Data,
  IUserInfo,
  IUserInfoStoreData,
  LocalVar,
  SpecialOfferType,
  SubscriptionPaymentMethodEnum,
} from "@/models/IUserInfo"
import stores from "@/stores/index"
import { canIUseBetaFeature, setBetaFeatures } from "@/utils/BetaFeature"
import { isLimitedAccessScope } from "@/utils/customerAuthGate"
import { DevAuthGateOverride, readDevAuthGate, writeDevAuthGate } from "@/utils/customerAuthGateDev"

class UserInfoStore implements IUserInfoStoreData {
  // @observable public name: string = "world"

  // 是否加载了用户数据信息
  @observable public isLoadData: boolean = false

  // 是否初始化了首页用户引导
  @observable public quickSetupInitialized: boolean = false

  // 客户授权管控的开发环境模拟开关（仅 dev 构建生效，生产恒为 null）
  @observable public devAuthGate: DevAuthGateOverride = readDevAuthGate()
  @observable local: Record<keyof LocalVar, any> = {
    showFreeAdd: true,
    showQuotaRemain: true,
  }
  @observable quickSetupData: SetupGuideData["setup_guide"] = {
    display: true,
    task: [],
  }
  @observable data: Data = {
    beta_features: {
      features: [],
      report_url: "",
    },
    banner_discount_v2024_04: {
      billing: false,
      billing_banner_url: "",
      dashboard: false,
      dashboard_banner_url: "",
      from: 1,
      main_banner_url: "",
      second_banner_url: "",
    },
    discount: undefined,
    plan_interval: AppPricingInterval.Every30_Days,
    store_name: "",
    export_email: "",
    total_order_count: 0,
    ShopName: "",
    last_30d_count: 0,
    notice_free_order_add: 0,
    show_double_free_quota_tip: true,
    can_remove_pp_branding: false,
    plan: 0,
    plan_member: 0,
    plan_name: "",
    plan_price: 0,
    quotaReminder: "",
    remaining_order_count: 0,
    selected_lang: SupportLanguages.English,
    shop_name: "",
    shop_plan_display_name: "",
    showNewUserGuide: false,
    show_pp_branding: false,
    store_real_name: "",
    special_offer: 0,
    txt: {},
    user_email: "",
    user_name: "",
    discount_start: false,
    buttonSwitchAll: {},
    money_with_currency_format: "${{amount}} USD",
    money_in_emails_format: "${{amount}}",
    is_paypal_connected: false,
    timezone: "",
    billing: {
      quota_reset_date: "",
    },
    current_subscription: {
      version: 2,
    },
    current_discount: {
      code: "",
      interval: AppPricingInterval.Every30_Days,
    },
    banner_data: [],
    currency: "USD", // 店铺的货币简码，为 null 则使用默认值 USD
    is_pi_subscription_rebate_opt_in: false,
  }
  // 用户折扣码
  @observable discount: {
    initialized: boolean
    code: CurrentDiscountCode | null
  } = {
      initialized: false,
      code: null,
    }

  constructor() {
    // 使用这个才会在 MobX 6 上才会更新视图
    makeObservable(this)
  }

  /**
   * 当前套餐等级
   */
  @computed
  get currentPlanLevel() {
    return this.data.plan_member
  }
  /**
   * 当前套餐等级
   */
  @computed
  get showBookDemoPopup() {
    return this.data.popups?.some(item => item.id === 30203)
  }
  /**
   * 当前套餐等级
   */
  @computed
  get isPPReturnBundle() {
    return this.data.subscription_payment_method === SubscriptionPaymentMethodEnum.PARCELPANEL
  }

  /**
   * 获取报告 URL
   */
  @computed
  get betaFeatureReportURL(): string {
    return this.data.beta_features.report_url
  }
  /**
   * 获取是否显示CrossSell Banner
   */
  @computed
  get showCrossSellBanner(): boolean {
    const targetBanner = this.data.banner_data.find(item => item.id === BannerEnum.CrossSale)

    return !!this.data.cross_sell_eligible && !!targetBanner && targetBanner.display
  }

  /**
   * 获取剩余额度百分比  0-100 区间
   */
  @computed
  get consumeOrderCountPercentage(): number {
    return (this.data.remaining_order_count / (this.data.total_order_count || 0)) * 100
  }

  @computed
  get showQuotaNotice(): boolean {
    // 额度不足提醒
    return !!this.data.quotaReminder
  }

  @computed
  get showPartnerFriendly(): boolean {
    // 用户店铺版本满足两种情况中的一种
    return (this.data.notice_partner_f !== 1 && this.data.is_show_partner_friendly_banner === 1) || (this.data.notice_partner_d !== 1 && this.data.is_show_partner_friendly_banner === 2)
  }

  @computed
  get showPartnerFriendlyAll(): boolean {
    return this.data.is_show_partner_friendly_banner !== 0
  }

  @computed
  get showReviewGuide(): boolean {
    return !this.showQuotaNotice && (this.data?.show_review_guide || false) && (this.data.notice_fou === 0)
  }

  @computed
  get showGetHelpReviewGuide(): boolean {
    return !!this.data?.show_review_guide && this.data.notice_fou === 0
  }

  @computed
  get showFirstSync(): boolean {
    return this.data.notice_fiv === 0 && !this.data.reg_gt_1day
  }

  @computed
  get isFirstSyncing(): boolean {
    return this.data.notice_firsync !== 1
  }

  @computed
  get showSharingBanner(): boolean {
    return this.data.notice_sharing === 0
  }

  @computed
  get showRemoveBranding(): boolean {
    if (!this.data.showPPBranding) return false
    return this.data.notice_remove_branding === 0
  }

  @computed
  get showFreeOrderAdd(): boolean {
    return this.data.notice_free_order_add === 0
  }

  @computed
  get showBeginnersGuide(): boolean {
    return this.data.notice_twe === 0
  }

  @computed
  get isFreeUser(): boolean {
    return this.data.plan_member === 1
  }

  @computed
  get isOldUser(): boolean {
    return this.data.plan_member === 0
  }

  @computed
  get oldPayingUsers(): boolean {
    return this.isOldUser && this.data.plan >= 1
  }

  /**
   * @since 2025-06
   * book a demo banner显示
   */
  @computed
  get getBookDemoBanner() {
    return this.data.banner_data.find(item => item.id === BannerEnum.BOOK_A_DEMO_2025_06)
  }

  // 是否有PPI资格
  @computed
  get isPPIEligible() {
    return this.data.is_pi_eligible
  }

  // 是否未参加PPI减免活动
  @computed
  get isNotPPIWavierActivity() {
    return !this.data.is_pi_subscription_rebate_opt_in
  }


  // 获取banner的状态
  @action.bound
  getBannerInfoById(id: BannerEnum) {
    return this.data.banner_data.find(item => item.id === id)
  }

  // 查看某个banner是否展示
  @action.bound
  isShowBanner(id: BannerEnum) {
    return this.getBannerInfoById(id)?.display ?? false
  }

  // 关闭某个banner
  @action.bound
  async closeBannerById(id: BannerEnum) {
    const res = await settingsApi.patchBanner(id, false)

    if (res.data.code === 200) {
      // 之前用的是this.setBannerClose这个方法，但是在settings页面有问题，所以这里直接setData了
      const newData = cloneDeep(this.data)
      const item =  newData.banner_data.find(item => item.id === id)

      if (item){
        item.display = false
        this.setData(newData)
      }
    }
  }

  /**
   * BCFM营销推广
   */
  @computed
  get showCWILLBundleBanner() {
    const banner = this.data.banner_data.find(item => item.id === 28692)

    return banner && banner.display
  }

  /**
   * 是否处于超管后台环境（非 Shopify admin app）
   * 客户授权管控只在这个环境下生效，商家内嵌使用不受影响
   */
  get isAdminContext(): boolean {
    return this.devAuthGate.fromAdmin ?? window.isFromAdmin === true
  }

  /**
   * 客户是否授权（合规）
   * 判定来自进入链接上的 access_scope：带 access_scope=limited 即未授权
   */
  get isAuth(): boolean {
    return this.devAuthGate.isAuth ?? !isLimitedAccessScope()
  }

  /**
   * 是否处于「超管后台白名单」模式：超管后台代客进入 且 客户未授权
   * 为 true 时只注册 ADMIN_SCOPED_ROUTES 里的路由，菜单同步收窄；
   * 客户已授权时超管后台同样是全量路由
   */
  get isAdminScoped(): boolean {
    return this.isAdminContext && !this.isAuth
  }

  /**
   * 订单详情是否以抽屉形式弹出
   * 原有 beta 灰度开关保持不变；另外只在「超管后台 + 客户未授权」的白名单模式下走抽屉
   */
  get useShipmentDrawer(): boolean {
    return canIUseBetaFeature(BetaFeature.NEW_SHIPMENT_INFO_PAGE) || this.isAdminScoped
  }

  @computed
  get showRemoveBrandingBanner(): boolean {
    // 无权编辑 & show branding
    return !this.data.can_remove_pp_branding && this.data.show_pp_branding
  }

  @action.bound
  public setBannerClose(id: number): void {
    const banners = cloneDeep(this.data.banner_data)

    banners.forEach(item => {
      if (item.id === id) {
        item.display = false
      }
    })

    this.setDataKey("banner_data", banners)
  }

  @action.bound
  public setData(data?: Data): void {
    if (!data) return
    /**
     * 兼容货币格式中带动态货币缩写 UID 378008
     */
    if (data.money_with_currency_format.includes("{{currency}}")){
      data.money_with_currency_format = data.money_with_currency_format.replaceAll("{{currency}}", data.currency)
    }
    if (data.money_in_emails_format.includes("{{currency}}")){
      data.money_in_emails_format = data.money_in_emails_format.replaceAll("{{currency}}", data.currency)
    }

    this.data = data
    this.setUpgradeBtnText(data, i18n.t)

    const shopLocale = getLocaleByShopifyCode(window.shopifyLocale)

    // 初始化EDD的app block状态
    stores.PrePurchaseEDDStore.setAppBlockActive(!!data.pre_edd_app_block_active_state)

    if (data.selected_lang){
      stores.commonStore.setActiveLanguage(data.selected_lang)
    } else {
      const locale = isSupportedLanguage(shopLocale) ? shopLocale : SupportLanguages.English

      stores.commonStore.setActiveLanguage(locale as SupportLanguages, true)
    }
  }

  // 更新用户信息
  @action.bound
  public updateUserInfo(): void {
    appApi.userInfo()
      .then(({ data: { code, data } }: { data: IUserInfo }) => {
        if (code === APICode.OK) {
          this.setData(data)
          this.setIsLoadData(true)

          if (data?.beta_features) {
            setBetaFeatures(data?.beta_features)
          }
        }
      })
  }
  // 更新SpecialOffer显示状态
  @action.bound
  public setSpecialOffer(status: SpecialOfferType): void {
    this.setDataKey("special_offer", status)
    appApi.setSpecialOffer(status)
  }

  @action.bound
  public setUpgradeBtnText(data: Data, t: TFunction): void {
    let btnText = ""

    if ((data.trial_days || 0) > 0) {
      btnText = t("StartTrialDaysFree", { ns: "common", replace: { trialDays: data.trial_days } })
    } else {
      btnText = t("UpgradeNow", { ns: "common" })
      // btnText = t("planInfo.changePlan", { ns: "dashboard" })
    }

    // 设置升级按钮文本
    stores.upgradeStore.setBtnText(btnText)
  }

  @action.bound
  setDataSingle<K extends keyof Data>(key: string, val: Data[K]): void {
    Object.prototype.hasOwnProperty.call(this.data, key) && (this.data[key] = val)
  }
  @action.bound
  setExportEmail(email:string): void {
    this.data.export_email = email
  }

  @action.bound
  setDiscountSingle<K extends keyof Data["discount"]>(key: K, val: Data["discount"][K]): void {
    Object.prototype.hasOwnProperty.call(this.data.discount, key) && (this.data.discount[key] = val)
  }

  @action.bound
  setDataKey<K extends keyof Data>(key: K, val: Data[K]): void {
    Object.prototype.hasOwnProperty.call(this.data, key) && (this.data[key] = val)
  }

  /**
   * 设置开发环境模拟开关（超管后台环境 / 客户授权状态）
   * 仅 dev 构建生效，写入 localStorage 后刷新页面依然保留
   */
  @action.bound
  public setDevAuthGate(patch: Partial<DevAuthGateOverride>): void {
    this.devAuthGate = { ...this.devAuthGate, ...patch }
    writeDevAuthGate(this.devAuthGate)
  }

  @action.bound
  setButtonSwitchAll<K extends keyof Data["buttonSwitchAll"]>(key: K, val: Data["buttonSwitchAll"][K]): void {
    this.data.buttonSwitchAll[key] = val
  }

  @action.bound
  setLocalVar(key: keyof LocalVar, val: any): void {
    Object.prototype.hasOwnProperty.call(this.local, key) && (this.local[key] = val)
  }

  @action.bound
  public setIsLoadData(status: boolean): void {
    this.isLoadData = status
  }

  @action.bound
  public setQuickSetupData(data: any): void {
    this.quickSetupData = data
    this.quickSetupInitialized = true
  }

  @action.bound
  public setDiscount(code: CurrentDiscountCode | null) {
    // 兼容如果后端传空数组的情况
    if (Array.isArray(code) && code.length === 0) code = null
    this.discount = {
      initialized: true,
      code,
    }
  }
  @action.bound
  public closeReviewBanner() {
    dashboard.clickReviewBanner().then(() => {
      // 兼容如果后端传空数组的情况
      this.data.show_review_guide = false
    })
  }
}

export default UserInfoStore
